<script lang="ts">
  // Auto sign-out after inactivity — protects ePHI on a shared or unattended device.
  // A warning appears 1 minute before logout with a live countdown; the user can
  // "Stay signed in" (resets the clock) or sign out immediately. Any real interaction
  // (click, key, scroll, touch, pointer move) resets the clock while no warning is up;
  // once the warning shows, staying signed in requires a deliberate click so a stray
  // mouse nudge on an unattended machine can't silently keep the session alive.
  import { onMount } from 'svelte';
  import { signOutUser } from '../lib/firebase';

  const IDLE_MS = 10 * 60 * 1000;     // sign out after 10 minutes idle
  const WARN_BEFORE_MS = 60 * 1000;   // warn 1 minute before that

  let deadline = Date.now() + IDLE_MS;   // wall-clock time we'd sign out if untouched
  let warnOpen = $state(false);
  let secsLeft = $state(60);

  function stay() { deadline = Date.now() + IDLE_MS; warnOpen = false; }
  function signOut() { signOutUser().catch(() => {}); }

  onMount(() => {
    let throttled = false;
    const onActive = () => {
      if (throttled || warnOpen) return;   // during the warning, require the button
      throttled = true;
      setTimeout(() => (throttled = false), 1500);
      deadline = Date.now() + IDLE_MS;
    };
    const EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel', 'mousemove'];
    for (const e of EVENTS) window.addEventListener(e, onActive, { passive: true });

    const iv = setInterval(() => {
      const left = deadline - Date.now();
      if (left <= 0) { signOut(); return; }
      if (left <= WARN_BEFORE_MS) { warnOpen = true; secsLeft = Math.max(0, Math.ceil(left / 1000)); }
      else if (warnOpen) warnOpen = false;
    }, 1000);

    return () => { clearInterval(iv); for (const e of EVENTS) window.removeEventListener(e, onActive); };
  });
</script>

{#if warnOpen}
  <div class="idle-scrim" aria-hidden="true"></div>
  <div class="idle-box" role="alertdialog" aria-modal="true" aria-labelledby="idle-h" aria-describedby="idle-d">
    <h2 id="idle-h">Still there?</h2>
    <p id="idle-d">You'll be signed out in <strong>{secsLeft}s</strong> to protect your health data.</p>
    <div class="idle-acts">
      <button class="idle-stay" onclick={stay}>Stay signed in</button>
      <button class="idle-out" onclick={signOut}>Sign out</button>
    </div>
  </div>
{/if}

<style>
  /* Above everything (nav/sheet/toast top out at 300 in app.css). */
  .idle-scrim { position: fixed; inset: 0; z-index: 1000; background: color-mix(in srgb, var(--bg) 55%, transparent); backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px); }
  .idle-box { position: fixed; z-index: 1001; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(92vw, 380px); background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-lg); padding: var(--space-5); text-align: center; box-shadow: 0 24px 60px -20px color-mix(in srgb, var(--bg) 92%, black); }
  .idle-box h2 { font-size: var(--fs-md); margin: 0 0 var(--space-2); }
  .idle-box p { color: var(--text-2); font-size: var(--fs-sm); margin: 0 0 var(--space-4); }
  .idle-box strong { color: var(--text); font-variant-numeric: tabular-nums; }
  .idle-acts { display: flex; gap: var(--space-2); justify-content: center; }
  .idle-acts button { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); font-weight: 700; font-size: var(--fs-sm); }
  .idle-stay { background: var(--accent-grad, var(--accent)); color: var(--on-accent, #fff); border: none; }
  .idle-out { background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line); }
  .idle-out:hover { color: var(--text); border-color: var(--line-strong); }
</style>
